import { F, getIntlTranslation } from '@/utils/intl';
import {
  AlignLeftOutlined,
  AppstoreOutlined,
  BorderOutlined,
  CodeOutlined,
  ColumnHeightOutlined,
  DownOutlined,
  FontSizeOutlined,
  GroupOutlined,
  IdcardOutlined,
  InsertRowAboveOutlined,
  InsertRowBelowOutlined,
  MenuOutlined,
  MessageOutlined,
  MinusOutlined,
  PictureOutlined,
  PlayCircleOutlined,
  RightOutlined,
  SearchOutlined,
  ShareAltOutlined,
  SplitCellsOutlined,
  ThunderboltOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { useDraggable } from '@dnd-kit/core';
import { Empty, Input, Tooltip } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import { PALETTE_DRAG_PREFIX } from './dragdrop';
import {
  PaletteGroup,
  PaletteGroupId,
  PaletteIconId,
  PaletteItem,
  paletteGroups,
} from './palette';
import { Registry } from './registry';

import styles from './PaletteRail.less';

const GROUP_TITLES: Record<PaletteGroupId, React.ReactNode> = {
  layout: (
    <F id="pages.emailCenter.visual.palette.layout" defaultMessage="Layout" />
  ),
  content: (
    <F id="pages.emailCenter.visual.palette.content" defaultMessage="Content" />
  ),
  custom: (
    <F
      id="pages.emailCenter.visual.palette.custom"
      defaultMessage="Your building blocks"
    />
  ),
};

/** One glyph per shape; see `paletteIcon` for how a block is matched to one. */
const ICON_GLYPHS: Record<PaletteIconId, React.ReactNode> = {
  section: <GroupOutlined />,
  columns: <SplitCellsOutlined />,
  heading: <FontSizeOutlined />,
  text: <AlignLeftOutlined />,
  image: <PictureOutlined />,
  button: <BorderOutlined />,
  divider: <MinusOutlined />,
  spacer: <ColumnHeightOutlined />,
  list: <UnorderedListOutlined />,
  menu: <MenuOutlined />,
  social: <ShareAltOutlined />,
  video: <PlayCircleOutlined />,
  quote: <MessageOutlined />,
  card: <IdcardOutlined />,
  header: <InsertRowAboveOutlined />,
  footer: <InsertRowBelowOutlined />,
  html: <CodeOutlined />,
  block: <AppstoreOutlined />,
};

/** Below this many blocks the filter is noise — scanning the grid is faster. */
const SEARCH_THRESHOLD = 10;

/** Which groups the user folded away, remembered across editor sessions. */
const COLLAPSED_STORAGE_KEY = 'emailCenter.visual.palette.collapsed';

function readCollapsed(): Set<PaletteGroupId> {
  try {
    const raw = window.localStorage.getItem(COLLAPSED_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return new Set(Array.isArray(parsed) ? (parsed as PaletteGroupId[]) : []);
  } catch {
    // A private-mode or quota failure must not take the palette down with it.
    return new Set();
  }
}

function writeCollapsed(ids: ReadonlySet<PaletteGroupId>): void {
  try {
    window.localStorage.setItem(
      COLLAPSED_STORAGE_KEY,
      JSON.stringify([...ids]),
    );
  } catch {
    // Nothing to do — the fold state is a convenience, not state we own.
  }
}

/**
 * One palette entry: a tile that inserts on click and a drag source for dropping
 * at a chosen position.
 *
 * Both gestures stay, and click is the one that must never regress — it is the
 * keyboard-reachable path, and dragging into a deeply nested empty column is
 * fiddly with a mouse. The sensor's distance constraint (see `VisualEditorBody`)
 * is what keeps a click from being read as a tiny drag.
 *
 * The label wraps to two lines and only then truncates. A single ellipsised line
 * was cutting names like "Xxxl · Appointment summary" down to "Xxxl · Appo…",
 * which is not a name anyone can pick a block by — and the tooltip is a hover
 * away, so it cannot be the primary way to read the list.
 */
/**
 * `general/partials/footer` → `general/partials`. A key with no folder keeps its
 * own name, so something is always shown rather than an empty line.
 */
function fragmentKeyFolder(key: string): string {
  const cut = key.lastIndexOf('/');
  return cut > 0 ? key.slice(0, cut) : key;
}

const PaletteEntry: React.FC<{
  item: PaletteItem;
  disabled?: boolean;
  onInsert: (type: string) => void;
  /**
   * Another tile carries the same label, so this one shows its key.
   *
   * Only then: the Layout & Components tab prints a key under every name, which it
   * can afford in a table row. On a palette tile the key is a second line on an
   * already small target, so it is spent where it is the only thing telling two
   * entries apart — two "Footer", two "Greeting", two "Header".
   */
  ambiguous?: boolean;
}> = ({ item, disabled, onInsert, ambiguous }) => {
  const intl = useIntl();
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${PALETTE_DRAG_PREFIX}${item.type}`,
    data: { kind: 'palette', type: item.type },
    disabled,
  });

  const tile = (
    <button
      ref={setNodeRef}
      type="button"
      className={styles.item}
      disabled={disabled}
      data-dragging={isDragging ? '1' : undefined}
      onClick={() => onInsert(item.type)}
      {...attributes}
      {...listeners}
    >
      <span className={styles.itemIcon} aria-hidden>
        {ICON_GLYPHS[item.icon]}
      </span>
      <span className={styles.itemLabel}>{item.label}</span>
      {ambiguous && item.fragmentKey && (
        /* The folder, not the whole path: two tiles that share a label also share
           the last segment ("…/footer" and "…/footer"), so the leading part is the
           only half that tells them apart — and it fits. The full key stays in the
           tooltip. */
        <span className={styles.itemKey} title={item.fragmentKey}>
          {fragmentKeyFolder(item.fragmentKey)}
        </span>
      )}
      {/* An inferred schema is the editor's guess, not a contract the fragment
          author kept — worth saying, since a wrong guess shows up as a
          mislabelled control rather than an error. A corner glyph rather than an
          "auto" chip: the word cost a third of the tile's width to mark the
          exception, and the tile's own tooltip carries the explanation. */}
      {item.inferred && (
        <span
          className={styles.inferredBadge}
          aria-label={getIntlTranslation(intl, {
            id: 'pages.emailCenter.visual.palette.inferred',
            defaultMessage: 'auto',
          })}
        >
          <ThunderboltOutlined />
        </span>
      )}
    </button>
  );

  return (
    <Tooltip
      // The full name plus the exact key: two partials that humanize to the same
      // friendly name are only tellable apart by their path.
      title={
        <>
          <div>{item.label}</div>
          {item.fragmentKey && (
            <div className={styles.tooltipKey}>{item.fragmentKey}</div>
          )}
          {item.inferred && (
            <div className={styles.tooltipHint}>
              <F
                id="pages.emailCenter.visual.palette.inferredHint"
                defaultMessage="This block does not describe its own settings, so they were worked out from its code. Declaring them in the building-block editor makes them exact."
              />
            </div>
          )}
        </>
      }
      mouseEnterDelay={0.4}
      // A tooltip left hanging over the canvas during a drag covers the drop
      // line it is aiming at.
      open={isDragging ? false : undefined}
    >
      {tile}
    </Tooltip>
  );
};

interface PaletteRailProps {
  registry: Registry;
  /** Resolved manifest labels from GET /email-component-labels. */
  labels?: Record<string, string>;
  disabled?: boolean;
  /** Adds the block at the end of the document (or into the selection's slot). */
  onInsert: (type: string) => void;
}

/**
 * The insert rail. Its contents come from the registry, which comes from the
 * fragment catalog — so shipping a `.twig` component adds a palette entry with no
 * change here (see `palette.ts` for what is filtered out and why).
 *
 * Groups collapse, and the collapsed set is remembered: a partner with thirty
 * building blocks otherwise pushes the structure tree off the bottom of the rail
 * every time the editor opens, and the tree is how you reach a block the canvas
 * cannot offer.
 */
const PaletteRail: React.FC<PaletteRailProps> = ({
  registry,
  labels,
  disabled,
  onInsert,
}) => {
  const intl = useIntl();
  const groups: PaletteGroup[] = useMemo(
    () => paletteGroups(registry, labels),
    [registry, labels],
  );

  /** Labels carried by more than one tile — those are the ones that need their key. */
  const repeatedLabels = useMemo(() => {
    const seen = new Set<string>();
    const repeated = new Set<string>();
    for (const group of groups) {
      for (const item of group.items) {
        if (seen.has(item.label)) repeated.add(item.label);
        seen.add(item.label);
      }
    }
    return repeated;
  }, [groups]);

  const [collapsed, setCollapsed] =
    useState<ReadonlySet<PaletteGroupId>>(readCollapsed);
  const [query, setQuery] = useState('');

  const toggleGroup = useCallback((id: PaletteGroupId) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      writeCollapsed(next);
      return next;
    });
  }, []);

  const total = useMemo(
    () => groups.reduce((sum, group) => sum + group.items.length, 0),
    [groups],
  );

  /**
   * Filtering matches the friendly label *and* the key: someone who knows the
   * catalog types `xxxl`, someone who does not types "footer", and both should
   * land on the same tile.
   */
  const needle = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!needle) return groups;
    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) =>
          `${item.label} ${item.fragmentKey ?? ''}`
            .toLowerCase()
            .includes(needle),
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [groups, needle]);

  if (groups.length === 0) {
    return (
      <div className={styles.palette}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <F
              id="pages.emailCenter.visual.palette.empty"
              defaultMessage="No building blocks are available for this partner yet."
            />
          }
        />
      </div>
    );
  }

  return (
    <div className={styles.palette}>
      {total >= SEARCH_THRESHOLD && (
        <Input
          allowClear
          size="small"
          className={styles.search}
          prefix={<SearchOutlined />}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={getIntlTranslation(intl, {
            id: 'pages.emailCenter.visual.palette.search',
            defaultMessage: 'Search blocks',
          })}
        />
      )}

      {filtered.length === 0 ? (
        <div className={styles.noMatch}>
          <F
            id="pages.emailCenter.visual.palette.noMatch"
            defaultMessage="No block matches “{query}”."
            values={{ query: query.trim() }}
          />
        </div>
      ) : (
        filtered.map((group) => {
          // A search result that stayed folded away would read as "no matches".
          const open = needle ? true : !collapsed.has(group.id);
          return (
            <div key={group.id} className={styles.group}>
              <button
                type="button"
                className={styles.groupTitle}
                aria-expanded={open}
                onClick={() => toggleGroup(group.id)}
              >
                <span className={styles.groupChevron}>
                  {open ? <DownOutlined /> : <RightOutlined />}
                </span>
                <span className={styles.groupTitleText}>
                  {GROUP_TITLES[group.id]}
                </span>
                <span className={styles.groupCount}>{group.items.length}</span>
              </button>
              {open && (
                <div className={styles.items}>
                  {group.items.map((item) => (
                    <PaletteEntry
                      key={item.type}
                      item={item}
                      disabled={disabled}
                      onInsert={onInsert}
                      ambiguous={repeatedLabels.has(item.label)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default React.memo(PaletteRail);
